import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { AuthProvider, redirectToLogin, useAuth } from './auth';
import { ThemeProvider, ThemeToggle } from './theme';
import AdminSubmissions from './pages/AdminSubmissions';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminUsers from './pages/AdminUsers';
import ArchiveTreePage from './pages/ArchiveTreePage';
import ArchiveView from './pages/ArchiveView';
import Diagnostics from './pages/Diagnostics';
import Discovered from './pages/Discovered';
import Home from './pages/Home';
import MySubmissions from './pages/MySubmissions';
import Profile from './pages/Profile';
import Purged from './pages/Purged';
import SearchPage from './pages/Search';
import SubmitArchive from './pages/SubmitArchive';
import TagDetailPage from './pages/TagDetail';
import TagsPage from './pages/Tags';
import Trash from './pages/Trash';

/** Internet Sociology mark — "S with i cut out", same geometry as the
 *  favicon but rendered with currentColor so it adopts the surrounding
 *  link colour. The favicon stays dark navy because it's drawn on light
 *  backgrounds (browser tabs); the header sits on near-black so it
 *  needs the inverse. */
function ISLogo({ size = 22 }: { size?: number }) {
  // Source viewBox is 200×220 (taller than wide). Scale by height so the
  // logo lines up with text x-height; width follows from the aspect.
  const w = Math.round((size * 200) / 220);
  return (
    <svg
      width={w}
      height={size}
      viewBox="0 0 200 220"
      role="img"
      aria-label="Internet Sociology"
      style={{ display: 'block' }}
    >
      <defs>
        <mask id="is-logo-cutout">
          <rect width="200" height="220" fill="white" />
          <circle cx="100" cy="44" r="26" fill="black" />
          <rect x="88" y="96" width="24" height="92" rx="12" fill="black" />
        </mask>
      </defs>
      <path
        d="M 136,68 C 136,36 64,36 64,68 C 64,98 148,104 148,140 C 148,180 52,180 52,150"
        stroke="currentColor"
        strokeWidth={28}
        fill="none"
        strokeLinecap="round"
        mask="url(#is-logo-cutout)"
      />
    </svg>
  );
}

/** Pill-style count next to an admin nav link. Hidden when count is 0
 *  so the navbar stays quiet when there's nothing to do. */
function NavBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span style={{
      display: 'inline-block',
      marginLeft: 6,
      padding: '1px 6px',
      background: '#c33',
      color: '#fff',
      borderRadius: 9,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: '14px',
      verticalAlign: 'baseline',
    }}>{count}</span>
  );
}

/** Compact search box in the header. Pressing Enter (or clicking the
 *  button) navigates to /search?q=... — the Search page owns the actual
 *  fetch and result rendering. */
function HeaderSearch() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = draft.trim();
    if (q.length < 2) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <form onSubmit={onSubmit} style={{ marginLeft: 12, display: 'flex', gap: 4 }}>
      <input
        type="search"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Search…"
        style={{ width: 160, padding: '4px 8px', fontSize: 13 }}
        aria-label="Search archived content"
      />
    </form>
  );
}

function Header() {
  const { user, logout, reviewCounts } = useAuth();
  const isAdmin = user?.role === 'admin';
  return (
    <header>
      <h1>
        <a
          href="https://internetsociology.org/"
          title="Back to Internet Sociology"
          style={{
            color: '#fff',
            marginRight: 10,
            textDecoration: 'none',
            opacity: 0.75,
            display: 'inline-flex',
            verticalAlign: 'middle',
          }}
        >
          <ISLogo size={22} />
        </a>
        <Link to="/" style={{ color: '#fff' }}>Social Archive</Link>
      </h1>
      <nav>
        <Link to="/">Archives</Link>
        <Link to="/tags">Tags</Link>
        <Link to="/submit">Submit a site</Link>
        <Link to="/my-submissions">My submissions</Link>
        {isAdmin && (
          <Link to="/admin/submissions">
            Review<NavBadge count={reviewCounts.submissions} />
          </Link>
        )}
        {isAdmin && <Link to="/diagnostics">Diagnostics</Link>}
        {isAdmin && <Link to="/discovered">Discovered</Link>}
        {isAdmin && <Link to="/trash">Trash</Link>}
        {isAdmin && <Link to="/purged">Purged</Link>}
        {isAdmin && (
          <Link to="/admin/users">
            Users<NavBadge count={reviewCounts.users} />
          </Link>
        )}
        <Link to="/profile">Profile</Link>
        <HeaderSearch />
      </nav>
      <div className="user">
        {user ? <>{user.email} <button className="secondary" onClick={logout}>Sign out</button></> : null}
        <ThemeToggle />
      </div>
    </header>
  );
}

/** Bounce the browser to the central login app. Rendered briefly on
 *  guest visits to gated routes; the user typically never sees it
 *  because navigation fires immediately. */
function LoginBounce() {
  useEffect(() => { redirectToLogin(); }, []);
  return <main><p className="muted">Redirecting to sign in…</p></main>;
}

/** Any logged-in user — including pending. Used for Profile so pending
 *  users can edit while waiting. */
function Authed({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <main><p className="muted">Loading…</p></main>;
  if (!user) return <LoginBounce />;
  return <>{children}</>;
}

/** Approved users only — pending users get bounced to Profile so they
 *  can see the pending banner instead of bare 403s. */
function Approved({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <main><p className="muted">Loading…</p></main>;
  if (!user) return <LoginBounce />;
  if (user.approved_at === null) return <Navigate to="/profile" replace />;
  return <>{children}</>;
}

/** Admin only — viewers (approved non-admin) get bounced to the archive
 *  list, which they can read but not mutate. */
function AdminOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <main><p className="muted">Loading…</p></main>;
  if (!user) return <LoginBounce />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

function Shell() {
  const { user } = useAuth();
  return (
    <>
      {user && <Header />}
      <main>
        <Routes>
          <Route path="/profile" element={<Authed><Profile /></Authed>} />
          <Route path="/" element={<Approved><Home /></Approved>} />
          <Route path="/archives/:id" element={<Approved><ArchiveView /></Approved>} />
          <Route path="/archives/:id/tree" element={<Approved><ArchiveTreePage /></Approved>} />
          <Route path="/submit" element={<Approved><SubmitArchive /></Approved>} />
          <Route path="/my-submissions" element={<Approved><MySubmissions /></Approved>} />
          <Route path="/diagnostics" element={<AdminOnly><Diagnostics /></AdminOnly>} />
          <Route path="/discovered" element={<AdminOnly><Discovered /></AdminOnly>} />
          <Route path="/trash" element={<AdminOnly><Trash /></AdminOnly>} />
          <Route path="/purged" element={<AdminOnly><Purged /></AdminOnly>} />
          <Route path="/admin/users" element={<AdminOnly><AdminUsers /></AdminOnly>} />
          <Route path="/admin/users/:id" element={<AdminOnly><AdminUserDetail /></AdminOnly>} />
          <Route path="/admin/submissions" element={<AdminOnly><AdminSubmissions /></AdminOnly>} />
          <Route path="/search" element={<Approved><SearchPage /></Approved>} />
          <Route path="/tags" element={<Approved><TagsPage /></Approved>} />
          <Route path="/tags/:name" element={<Approved><TagDetailPage /></Approved>} />
        </Routes>
      </main>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Shell />
      </AuthProvider>
    </ThemeProvider>
  );
}
